import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SetupStep } from '../../reducer/integration.reducer';
import { RequestedPermission } from '../sharepoint-folders/sharepoint-folders.types';

/**
 * Modal step shown to the user. Mirrors `ModalViewStep` in the D365 BC connect modal:
 * there is deliberately no `created` step — the reducer reaching `created` closes this
 * modal and the manage modal takes over.
 */
export type SharepointConnectStep = 'welcome' | 'awaiting_consent' | 'error';

/**
 * REV-21860 — connect modal for the SharePoint card in Organization → Settings →
 * Integrations.
 *
 * Same contract as `D365BcConnectModalComponent`: it renders the pending-setup step
 * machine and emits `closed` when done. The difference is what the user supplies —
 * SharePoint needs NO configuration at all (no environment, no credentials, no server
 * URL): the tenant comes from whichever Microsoft work account signs in, so the form is
 * just an OPTIONAL display name plus the consent explainer.
 *
 * Not to be confused with the closing-scoped consent card inside
 * `SharepointFoldersContainerComponent`: that one connects a SINGLE closing's folder
 * bindings and lives on the closing page. Both end at the same per-org connection.
 */
@Component({
    selector: 'aumico-sharepoint-connect-modal',
    templateUrl: './sharepoint-connect-modal.component.html',
    styleUrls: ['./sharepoint-connect-modal.component.scss'],
})
export class SharepointConnectModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public step: SharepointConnectStep = 'welcome';
    public errorMessage: string | null = null;

    /** Optional: the BE defaults to `SharePoint (<tenant-prefix>)` when left blank. */
    public customDisplayName = '';

    public readonly vendorDefinition = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.SHAREPOINT,
    );

    /**
     * The scopes listed on the consent explainer. View configuration mirroring the scopes
     * the BE asks Microsoft for — the SAME keys the closing-scoped consent card uses, so
     * the two surfaces cannot drift apart in what they promise the user.
     */
    public readonly requestedPermissions: RequestedPermission[] = [
        {
            labelKey: 'aumico_integration_sharepoint_consent_permission_files_read',
            descriptionKey: 'aumico_integration_sharepoint_consent_permission_files_read_desc',
        },
        {
            labelKey: 'aumico_integration_sharepoint_consent_permission_sites_read',
        },
    ];

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.integrationProvider
            .getPendingSetup$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((setup) => {
                // Another vendor's flow must not drive this modal's step machine.
                if (setup.vendor !== IntegrationVendor.SHAREPOINT) return;

                this.errorMessage = setup.error;

                // The connection landed (detected generically in `loadConnectionsSuccess`).
                // Close immediately; the overview opens the manage modal on
                // `connectionJustCreated` so the user lands on the result.
                if (setup.step === 'created') {
                    this.closed.emit();
                    return;
                }

                this.step = this.viewStepFor(setup.step);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * "Mit Microsoft 365 verbinden" — starts the org connect.
     *
     * NOTE: the Microsoft popup does NOT open inside this click's call stack. The effect
     * first fetches the consent URL over HTTP and only then calls `window.open`, so the
     * open lands outside the user-gesture window and Chrome may suppress it on a slow
     * response — leaving the modal on `awaiting_consent` until the watcher times out.
     * This matches the D365 BC / Abacus / Topal connect flows exactly, so it is the
     * established behaviour here rather than something specific to SharePoint.
     *
     * If it does become a problem, the fix already exists in this lib: see REV-21057
     * BUG 2 in `sharepoint-file-picker.service.ts`, where the CALLER opens a blank popup
     * synchronously in its click handler and hands the window to the async code to
     * navigate. Applying that here means threading a pre-opened window from this method
     * through to `openConsentPopupAndWatch$`.
     */
    public onStartConsent(): void {
        this.integrationProvider.startSharepointOrgConnect(this.customDisplayName.trim());
    }

    public onRetryFromError(): void {
        this.errorMessage = null;
        this.step = 'welcome';
    }

    public onCancel(): void {
        this.integrationProvider.cancelSetup();
        this.closed.emit();
    }

    private viewStepFor(reducerStep: SetupStep): SharepointConnectStep {
        switch (reducerStep) {
            case 'awaiting_consent':
                return 'awaiting_consent';
            case 'error':
                return 'error';
            case 'created':
                // Handled by closing the modal above; should never render.
                return 'welcome';
            case 'idle':
            default:
                return 'welcome';
        }
    }
}
